import { NotificationItem } from '@/components/NotificationTable/types';
import { getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { PostHog } from 'posthog-js';
import { useCallback, useEffect, useRef } from 'react';
import {
  getInboxItemType,
  getTimeSinceCreatedSeconds,
  trackFilterApplied,
  trackInboxItemOpened,
  trackInboxMonitoringStarted,
  trackInboxMonitoringStopped,
} from '../services/analytics';

interface UseInboxTrackingParams {
  posthog: PostHog;
  page: 'inbox' | 'archive';
  typeFilter: string;
  searchTerm: string;
  notifications: NotificationItem[];
  selectedProvider: Practitioner | null;
  currentUser: Practitioner | null;
}

interface UseInboxTrackingReturn {
  handleRowClick: (notification: NotificationItem, defaultHandler: (notification: NotificationItem) => void) => void;
}

/**
 * Centralizes tracking logic for InboxPage and ArchivePage
 * Handles filter tracking, inbox item opened tracking, and provider monitoring tracking
 */
export function useInboxTracking({
  posthog,
  page,
  typeFilter,
  searchTerm,
  notifications,
  selectedProvider,
  currentUser,
}: UseInboxTrackingParams): UseInboxTrackingReturn {
  const prevSelectedProviderRef = useRef<Practitioner | null>(null);

  // Track filter changes
  useEffect(() => {
    if (typeFilter !== 'all' || searchTerm) {
      const filterTypes: string[] = [];
      if (typeFilter !== 'all') {
        filterTypes.push(`type:${typeFilter}`);
      }
      if (searchTerm) {
        filterTypes.push(`search:${searchTerm}`);
      }

      trackFilterApplied(posthog, {
        page,
        filter_types: filterTypes,
        results_count: notifications.length,
      });
    }
  }, [typeFilter, searchTerm, notifications.length, posthog, page]);

  // Track provider monitoring changes
  useEffect(() => {
    const prevProvider = prevSelectedProviderRef.current;
    const currentSelectedProvider = selectedProvider;

    // Started monitoring
    if (!prevProvider && currentSelectedProvider && currentUser) {
      const isMonitoringAnother = getReferenceString(currentSelectedProvider) !== getReferenceString(currentUser);
      if (isMonitoringAnother) {
        trackInboxMonitoringStarted(posthog, {
          monitored_practitioner_id: currentSelectedProvider.id!,
          view_type: page,
        });
      }
    }

    // Stopped monitoring
    if (prevProvider && !currentSelectedProvider) {
      trackInboxMonitoringStopped(posthog, {
        monitored_practitioner_id: prevProvider.id!,
        view_type: page,
      });
    }

    // Changed monitored provider
    if (
      prevProvider &&
      currentSelectedProvider &&
      getReferenceString(prevProvider) !== getReferenceString(currentSelectedProvider)
    ) {
      trackInboxMonitoringStopped(posthog, {
        monitored_practitioner_id: prevProvider.id!,
        view_type: page,
      });

      if (currentUser && getReferenceString(currentSelectedProvider) !== getReferenceString(currentUser)) {
        trackInboxMonitoringStarted(posthog, {
          monitored_practitioner_id: currentSelectedProvider.id!,
          view_type: page,
        });
      }
    }

    prevSelectedProviderRef.current = currentSelectedProvider;
  }, [selectedProvider, currentUser, posthog, page]);

  // Track inbox item opened
  const handleRowClick = useCallback(
    (notification: NotificationItem, defaultHandler: (notification: NotificationItem) => void) => {
      const patientId = notification.task.for?.reference?.split('/')[1];
      if (patientId) {
        trackInboxItemOpened(posthog, {
          source: page === 'inbox' ? 'inbox row' : 'archive row',
          inbox_item_type: getInboxItemType(notification),
          is_read: notification.isRead,
          time_since_created_seconds: getTimeSinceCreatedSeconds(notification.timestamp),
          patient_id: patientId,
        });
      }

      defaultHandler(notification);
    },
    [posthog, page]
  );

  return {
    handleRowClick,
  };
}
